import React, { useMemo } from 'react';
import { Trees } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import {
    useGetSiteFilterOptionsQuery,
    useGetPortfolioStateOfNatureQuery,
    type GqlRiskBandLevel,
    type GqlSonBandLevel,
} from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import SonFactorsPanel from 'src/shared/components/son-factors-panel/SonFactorsPanel';
import useGnrFilters from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrFilters';
import { sanitizeSelectedFilters } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import GnrActiveFilters from '../site-list/components/filters/GnrActiveFilters';
import SiteListFilters from '../site-list/components/filters/SiteListFilters';

const FEATURE_ID = 'risk-insights';

const GnrStateOfNature = () => {
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });
    const { currentCustomerId } = useCustomer();
    const { selectedFilters, applyFilters, resetFilters, updateFilters } = useGnrFilters();

    const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetSiteFilterOptionsQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );

    const siteFilterOptions = filterOptionsData?.organisation?.siteFilterOptions;

    const validatedFilters = useMemo(
        () => sanitizeSelectedFilters(selectedFilters, siteFilterOptions),
        [selectedFilters, siteFilterOptions]
    );

    const isicFilter = validatedFilters.find(f => f.name === 'isic')?.values;
    const countryFilter = validatedFilters.find(f => f.name === 'country')?.values;
    const physicalRiskLevels = validatedFilters.find(f => f.name === 'physical_risk_levels')?.values as GqlRiskBandLevel[] | undefined;
    const dependencyLevels = validatedFilters.find(f => f.name === 'dependency_levels')?.values as GqlRiskBandLevel[] | undefined;
    const impactLevels = validatedFilters.find(f => f.name === 'impact_levels')?.values as GqlRiskBandLevel[] | undefined;
    const sonLevels = validatedFilters.find(f => f.name === 'son_levels')?.values as GqlSonBandLevel[] | undefined;

    const filterArgs = {
        isic: isicFilter,
        countries: countryFilter,
        physicalRiskLevels,
        dependencyLevels,
        impactLevels,
        sonLevels,
    };

    const { data: stateOfNatureData, isLoading: isLoadingStats } = useGetPortfolioStateOfNatureQuery(
        { businessCentralId: currentCustomerId, ...filterArgs },
        { skip: !currentCustomerId }
    );

    const stats = stateOfNatureData?.organisation?.portfolioRiskStats;
    const categories = stats?.sonCategoryBreakdown ?? [];

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    return (
        <StandardAppLayout
            title='State of Nature'
            subTitle='Current biodiversity conditions against global index'
            classNames={{ otherActions: '!justify-start !p-0' }}
            stickyHeader={false}
            otherActions={
                <SiteListFilters
                    selectedFilters={selectedFilters}
                    onApply={applyFilters}
                    onClear={resetFilters}
                    filterOptions={siteFilterOptions}
                    isLoadingFilterOptions={isLoadingFilterOptions}
                    businessCentralId={currentCustomerId ?? undefined}
                />
            }
            mainContent={
                <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='gnr-state-of-nature-main'>
                    <GnrActiveFilters
                        selectedFilters={validatedFilters}
                        countries={siteFilterOptions?.countries}
                        isicCodes={siteFilterOptions?.isicCodes}
                        onRemove={updateFilters}
                        onClearAll={resetFilters}
                        matchingSiteCount={stats?.assessedSiteCount}
                    />

                    <div className='flex flex-row gap-4'>
                        <div className='flex-[2] flex flex-col'>
                            <RiskScoreCard
                                title='Overall State of Nature'
                                value={stats?.meanStateOfNature ?? null}
                                scoreType='Condition'
                                isLoading={isLoadingStats}
                                size='lg'
                                slug='state-of-nature'
                                icon={Trees}
                                className='h-full'
                            />
                        </div>
                        {categories.map(category => (
                            <div key={category.sourceKey} className='flex-1 flex flex-col'>
                                <RiskScoreCard
                                    title={category.name}
                                    value={category.score}
                                    scoreType='Condition'
                                    fullBadgeLabel={false}
                                    isLoading={isLoadingStats}
                                    size='md'
                                    slug={`son_${category.sourceKey}`}
                                    showBarometer={false}
                                    className='h-full'
                                />
                            </div>
                        ))}
                    </div>

                    {categories.some(c => (c.factors?.length ?? 0) > 0) && (
                        <div className='flex flex-col gap-3'>
                            <div className='flex items-center justify-between'>
                                <span className='text-xl text-black'>State of Nature category breakdown</span>
                                <ReportingFrameworks slugPrefix='gnr-son' />
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                {categories.map(category => (
                                    <SonFactorsPanel
                                        key={`${category.sourceKey}-factors`}
                                        title={category.name}
                                        slug={category.sourceKey}
                                        factors={(category.factors ?? [])
                                            .map(f => ({
                                                name: f.name,
                                                sourceKey: f.sourceKey,
                                                weight: f.weight,
                                                score: f.score ?? null,
                                            }))
                                            .sort((a, b) => {
                                                if (a.score === null && b.score === null) return 0;
                                                if (a.score === null) return 1;
                                                if (b.score === null) return -1;
                                                return b.score - a.score;
                                            })}
                                        isLoading={isLoadingStats}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default GnrStateOfNature;
